/**
 * Hooks for agent runs.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listRuns, getRun, stopRun } from '../api/agentRuns';
import { isTerminalRunStatus } from '../types/agentRun.types';
import type { AgentRun, AgentRunDetail } from '../types/agentRun.types';

const RUNS_KEY = ['agentRuns'];

/** List the caller's own runs (already owner-scoped server-side), in one project when given. */
export function useRuns(projectId: string | null = null): {
  data: AgentRun[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const query = useQuery<AgentRun[]>({
    queryKey: projectId === null ? RUNS_KEY : [...RUNS_KEY, 'byProject', projectId],
    queryFn: () => listRuns(projectId),
    refetchInterval: 5000,
  });

  return { data: query.data, isLoading: query.isLoading, error: query.error };
}

/** How often a live run's page polls: its owner (or an admin) every 4 s. */
export const RUN_POLL_MS = 4000;
/**
 * A project reader (no Stop) every 15 s: each of their GETs re-checks their
 * access in GitLab live (ruling 7), about three GitLab calls a time.
 */
export const READER_RUN_POLL_MS = 15_000;

/** A single run + its event timeline. Polls while the run is non-terminal. */
export function useRun(id: string): {
  data: AgentRunDetail | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const query = useQuery<AgentRunDetail>({
    queryKey: [...RUNS_KEY, id],
    queryFn: () => getRun(id),
    enabled: id.length > 0,
    refetchInterval: (q) => {
      const run = q.state.data;
      if (run && isTerminalRunStatus(run.status)) return false;
      return run?.can_stop === false ? READER_RUN_POLL_MS : RUN_POLL_MS;
    },
  });

  return { data: query.data, isLoading: query.isLoading, error: query.error };
}

export function useStopRun(): {
  mutateAsync: (id: string) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<undefined, Error, string>({
    mutationFn: async (id: string) => {
      await stopRun(id);
      return undefined;
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
      void queryClient.invalidateQueries({ queryKey: [...RUNS_KEY, id] });
    },
  });

  return { mutateAsync, isPending, error };
}
